import React, { useState, useCallback, useRef, useMemo } from 'react';
import { Drawer } from '@mantine/core';
import { IconSettings } from '@tabler/icons-react';
import type { NodeConfig, NodeRegistryEntry, DeclaredInput } from '../../types';
import type { CodeBlockOutput } from '../../types/editor';
import type { ResolvedType } from '../../utils/resolveCodeBlockTypes';
import { ValuesEditor } from './ValuesEditor';
import { InputsConfigEditor } from './InputsConfigEditor';
import { CodeBlockOutputsSection } from './CodeBlockOutputsSection';
import { DeclaredInputsSection } from './DeclaredInputsSection';
import classes from './NodeConfigDrawer.module.css';

const MIN_WIDTH = 320;
const MAX_WIDTH = 900;
const DEFAULT_WIDTH = 480;

export type NodeConfigDrawerProps = {
  open: boolean;
  onClose: () => void;
  selectedNodeId: string | null;
  nodeConfig: NodeConfig;
  registryEntry: NodeRegistryEntry | null;
  /** Resolved input types connected via edges (for CodeEditor type inference) */
  resolvedInputs?: ResolvedType[];
  /** Resolved output types configured for CodeBlock (for CodeEditor type inference) */
  resolvedOutputs?: ResolvedType[];
  onValuesChange: ( values: Record<string, unknown> ) => void;
  onInputsConfigChange?: ( inputsConfig: Record<string, unknown> ) => void;
  declaredInputs: DeclaredInput[];
  onDeclaredInputsChange: ( declaredInputs: DeclaredInput[]) => void;
  theme?: 'light' | 'dark';
};

/**
 * Right-side drawer for editing a selected node's configuration.
 * Opened/closed manually via props — does NOT auto-open on node selection.
 */
export function NodeConfigDrawer({
  open,
  onClose,
  selectedNodeId,
  nodeConfig,
  registryEntry,
  resolvedInputs,
  resolvedOutputs,
  onValuesChange,
  onInputsConfigChange,
  declaredInputs,
  onDeclaredInputsChange,
  theme
}: NodeConfigDrawerProps ) {
  const values = nodeConfig.values ?? {};
  const inputsConfig = nodeConfig.inputsConfig as Record<string, unknown> | undefined;
  const hasInputsConfig = inputsConfig && Object.keys( inputsConfig ).length > 0;

  const isCodeBlock = registryEntry?.name === 'CodeBlock';
  const codeBlockOutputs = ( values.outputs as CodeBlockOutput[] | undefined ) ?? [{ name: 'output', type: 'any' as const }];

  const [ width, setWidth ] = useState( DEFAULT_WIDTH );
  const draggingRef = useRef( false );

  const handleResizeStart = useCallback(( e: React.MouseEvent ) => {
    e.preventDefault();
    draggingRef.current = true;
    const startX = e.clientX;
    const startWidth = width;

    const onMove = ( ev: MouseEvent ) => {
      if ( !draggingRef.current ) return;
      const delta = startX - ev.clientX;
      setWidth( Math.min( MAX_WIDTH, Math.max( MIN_WIDTH, startWidth + delta )));
    };

    const onUp = () => {
      draggingRef.current = false;
      document.removeEventListener( 'mousemove', onMove );
      document.removeEventListener( 'mouseup', onUp );
    };

    document.addEventListener( 'mousemove', onMove );
    document.addEventListener( 'mouseup', onUp );
  }, [width]);

  const portalTarget = useMemo(() => {
    if ( typeof document === 'undefined' ) return undefined;
    let el = document.getElementById( 'rule-editor-drawer-portal' );
    if ( !el ) {
      el = document.createElement( 'div' );
      el.id = 'rule-editor-drawer-portal';
      document.body.appendChild( el );
    }
    el.setAttribute( 'data-theme', theme ?? 'dark' );
    return el;
  }, [theme]);

  return (
    <Drawer
      opened={open}
      onClose={onClose}
      position="right"
      size={width}
      withOverlay={false}
      trapFocus={false}
      title="Node Configuration"
      portalProps={{ target: portalTarget }}
      classNames={{
        content: classes.drawerContent,
        header: classes.drawerHeader,
        title: classes.drawerTitle,
        body: classes.drawerBody
      }}
    >
      <div className={classes.resizeHandle} onMouseDown={handleResizeStart} />
      {selectedNodeId && registryEntry ? (
        <>
          <div className={classes.nodeIdentity}>
            <div className={classes.nodeType}>{registryEntry.name}</div>
            <div className={classes.nodeId}>{selectedNodeId}</div>
            <span className={classes.categoryBadge}>{registryEntry.category}</span>
          </div>

          <div className={classes.sectionTitle}>Values</div>
          <ValuesEditor
            values={values}
            registryEntry={registryEntry}
            resolvedInputs={resolvedInputs}
            resolvedOutputs={resolvedOutputs}
            onValuesChange={onValuesChange}
            theme={theme}
          />

          {isCodeBlock && (
            <CodeBlockOutputsSection
              outputs={codeBlockOutputs}
              onChange={( newOutputs ) => {
                onValuesChange({ ...values, outputs: newOutputs });
              }}
            />
          )}

          {hasInputsConfig && onInputsConfigChange && (
            <>
              <div className={classes.sectionTitle} style={{ marginTop: 16 }}>Inputs Config</div>
              <InputsConfigEditor
                inputsConfig={inputsConfig}
                onChange={onInputsConfigChange}
              />
            </>
          )}

          <DeclaredInputsSection
            nodeId={selectedNodeId}
            inputs={registryEntry.inputs}
            declaredInputs={declaredInputs}
            onDeclaredInputsChange={onDeclaredInputsChange}
          />
        </>
      ) : (
        <div className={classes.placeholder}>
          <div className={classes.placeholderIcon}>
            <IconSettings size={20} />
          </div>
          <div className={classes.placeholderTitle}>
            Select a node to configure
          </div>
          <div className={classes.placeholderDescription}>
            Click on a node in the canvas to edit its values here.
          </div>
        </div>
      )}
    </Drawer>
  );
}
